import React from "react";
import { Isvg } from "../../components";
import { VstToggleButton } from "vstui";
import styled, { keyframes } from "styled-components";

const slideDown = keyframes`
  0% {
    display: none;
    opacity: 0;
  }
  1% {
    display: block;
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  0% {
    display: block;
    opacity: 1;
  }
  99% {
    display: block;
  }
  100% {
    display: none;
    opacity: 0;
  }
`;

const LabsDescriptionContainer = styled.div`
  animation: ${slideUp} ease 100ms;
  transition: height 100ms;
  height: ${props => (props.expanded ? `${props.childrenHeight}px;` : 0)};  
  display: ${props => (props.expanded ? "block" : "none")};
  &.expanded {
    animation: ${slideDown} ease 100ms;
  }
`;

export default class LabsItem extends React.Component {
  constructor(props) {
    super(props);
    const { enabled, rating } = Fraction.User.labs[this.props.lab.id];
    this.state = {
      enabled,
      rating,
      expanded: false,
      feedbackExpanded: false,
      feedbackText: "",
      feedbackSubmitted: false,
      childrenHeight: 0
    };

    this._clickLab = this._clickLab.bind(this);
    this._updateFeedback = this._updateFeedback.bind(this);
    this._submitFeedback = this._submitFeedback.bind(this);
    this._closeFeedback = this._closeFeedback.bind(this);
    this._openFeedback = this._openFeedback.bind(this);
    this._toggleLab = this._toggleLab.bind(this);
    this._enableLab = this._enableLab.bind(this);
    this._disableLab = this._disableLab.bind(this);
    this._likeLab = this._likeLab.bind(this);
    this._dislikeLab = this._dislikeLab.bind(this);
  }

  componentDidMount() {
    Jigsaw.Labs.getInfo(
      this.props.lab.id,
      function(response) {
        if (response.isSuccess()) {
          const { likes, dislikes } = response.data;
          this.setState({ likes, dislikes });
        }
      }.bind(this)
    );
  }

  _determineHeight() {
    const childrenHeight =
      [
        this.descriptionheader,
        this.learnMoreButton,
        this.leaveFeedbackButton,
        this.feedbackForm,
        this.feedbackThanks
      ].reduce((acc, ref) => {
        acc = acc + (ref ? ref.clientHeight : 0);
        return acc;
      }, 0) + 20;
    this.setState({ childrenHeight });
  }

  _clickLab() {
    // We want the text/container height to be checked here so the animations work correctly
    setTimeout(() => {
      this._determineHeight();
    }, 0);
    this.setState(prevState => ({ expanded: !prevState.expanded }));
  }

  _updateFeedback(e) {
    this.setState({ feedbackText: e.currentTarget.value });
  }

  _submitFeedback(e) {
    if (this.state.feedbackText.length > 0) {
      Fraction.Helpers.Labs.submitFeedback(
        this.state.feedbackText,
        this.props.lab.name,
        this.props.isbn
      );
      this.setState({ feedbackSubmitted: true }, this._determineHeight);
    }
  }

  _closeFeedback() {
    this.setState({ feedbackExpanded: false }, this._determineHeight);
  }

  _openFeedback(e) {
    this.setState({ feedbackExpanded: true }, this._determineHeight);
  }

  _ratingCallback(response, rating) {
    if (response.isSuccess()) {
      const labId = this.props.lab.id;
      Fraction.User.labs[labId].rating = rating;
      const { likes, dislikes } = response.data;
      this.setState({ likes, dislikes, rating });
    }
  }

  _enableLab(callback) {
    return Fraction.Helpers.Labs.enableLab(this.props.lab.id).then(() => {
      this.setState({ enabled: true });
    });
  }

  _disableLab(callback) {
    return Fraction.Helpers.Labs.disableLab(this.props.lab.id).then(() => {
      this.setState({ enabled: false });
    });
  }

  _toggleLab() {
    const promise = Fraction.User.labs[this.props.lab.id].enabled
      ? this._disableLab
      : this._enableLab;
    promise().then(this.props.lab.toggle);
  }

  _likeLab() {
    if (this.state.rating <= 0) {
      Jigsaw.Labs.like(this.props.lab.id, response => {
        this._ratingCallback(response, 1);
      });
    }
  }

  _dislikeLab() {
    if (this.state.rating >= 0) {
      Jigsaw.Labs.dislike(this.props.lab.id, response => {
        this._ratingCallback(response, -1);
      });
    }
  }

  _renderLabToggler() {
    if (this.props.lab.launch) {
      return (
        <button
          aria-label={i18n.t("labs.go-button", { lab: this.props.lab.name })}
          className="noButton go-button"
          onClick={this.props.lab.launch}
        >
          {i18n.t("base.launch")}
        </button>
      );
    }
    if (this.props.lab.toggle) {
      return (
        <VstToggleButton
          on={this.state.enabled}
          onToggle={this._toggleLab}
          aria-label={i18n.t("labs.toggle-button", {
            lab: this.props.lab.name
          })}
        />
      );
    }
    return <span />;
  }

  render() {
    const {
      category,
      lab: { description, id: lab_id, learnMore, name, star }
    } = this.props;
    const {
      expanded,
      feedbackExpanded,
      feedbackText,
      feedbackSubmitted,
      childrenHeight,
      likes,
      dislikes
    } = this.state;
    const chevron = expanded ? "up" : "down";
    return (
      <div className={`lab-item ${category}-item`} key={`lab-item-${lab_id}`}>
        <div className="lab-title">
          <button
            aria-expanded={expanded}
            className="noButton title"
            onClick={this._clickLab}
          >
            <span className="arrow-icon">
              <Isvg key={chevron} src={`/img/chevron_sm-${chevron}.svg`} />
            </span>
            <span className="lab-name">
              {name}
              {star && (
                <span className="lab-star">
                  <Isvg src="/img/star.svg" />
                </span>
              )}
            </span>
          </button>
          {this._renderLabToggler()}
        </div>

        <LabsDescriptionContainer
          aria-hidden={!expanded}
          className={`lab-description${expanded ? " expanded" : ""}`}
          expanded={expanded}
          childrenHeight={childrenHeight}
        >
          <div
            ref={h => {
              this.descriptionheader = h;
            }}
            className="description-header"
          >
            <div className="description-text">{description}</div>
            <div className="lab-ratings">
              <button
                aria-label={
                  likes === 0
                    ? i18n.t("labs.first-like")
                    : i18n.t("labs.like-feature", { likes: likes || 0 })
                }
                className={
                  (Fraction.User.labs[lab_id] &&
                  Fraction.User.labs[lab_id].rating > 0
                    ? "active"
                    : "") + " likes noButton"
                }
                onClick={this._likeLab}
              >
                <Isvg
                  className={
                    Fraction.User.labs[lab_id].rating > 0 ? "voted" : null
                  }
                  src="/img/thumbs_up_gray.svg"
                >
                  NS
                </Isvg>
                {likes || 0}
              </button>
              <button
                aria-label={
                  dislikes === 0
                    ? i18n.t("labs.first-dislike")
                    : i18n.t("labs.dislike-feature", {
                        dislikes: dislikes || 0
                      })
                }
                className={
                  (Fraction.User.labs[lab_id] &&
                  Fraction.User.labs[lab_id].rating < 0
                    ? "active"
                    : "") + " dislikes noButton"
                }
                onClick={this._dislikeLab}
              >
                <Isvg
                  className={
                    Fraction.User.labs[lab_id].rating < 0 ? "voted" : null
                  }
                  src="/img/thumbs_down_gray.svg"
                >
                  NS
                </Isvg>
                {dislikes || 0}
              </button>
            </div>
          </div>
          {learnMore && (
            <button
              ref={b => {
                this.learnMoreButton = b;
              }}
              className="lab-learn-more noButton"
              onClick={learnMore}
            >
              {i18n.t("registration.learn-more")}
            </button>
          )}
          {!feedbackExpanded && (
            <button
              ref={b => {
                this.leaveFeedbackButton = b;
              }}
              className={"submit lab-feedback noButton lightgray"}
              onClick={this._openFeedback}
            >
              {i18n.t("labs.leave-feedback")}
            </button>
          )}
          {feedbackExpanded &&
            (!feedbackSubmitted ? (
              <div
                ref={form => {
                  this.feedbackForm = form;
                }}
                className="feedback-area"
              >
                <textarea
                  rows="1"
                  onChange={this._updateFeedback}
                  aria-label={i18n.t("labs.what-did-you-think", { lab: name })}
                  placeholder={i18n.t("labs.what-did-you-think", { lab: name })}
                  value={feedbackText}
                />
                <div className="feedback-action-buttons">
                  <button className="noButton" onClick={this._closeFeedback}>
                    {i18n.t("base.cancel")}
                  </button>
                  <button className="submit" onClick={this._submitFeedback}>
                    {i18n.t("base.submit")}
                  </button>
                </div>
              </div>
            ) : (
              <div
                ref={thanks => {
                  this.feedbackThanks = thanks;
                }}
                className="feedback-area thanks"
              >
                <img src="/img/checkmark_success.svg" />
                <span>{i18n.t("labs.thanks-for-feedback")}</span>
              </div>
            ))}
        </LabsDescriptionContainer>
      </div>
    );
  }
}
